/**
 * MRR movement — month-over-month diff of two MRR snapshot sets.
 *
 * Pure (no I/O): the caller loads the prior + current month rows from
 * `mrr_snapshots` and passes them here. Movement is keyed by `source:account_id`
 * so the two revenue streams (recurring_transaction, won_deal) never collide:
 *
 *   new         — account present this month, absent last month
 *   churned     — account present last month, absent this month (lost MRR)
 *   expansion   — account in both, amount increased
 *   contraction — account in both, amount decreased
 *   netChange   — Σ current − Σ prior
 *
 * ## Why this cannot be derived from live rows
 *
 * A cancelled `recurring_transactions` row is HARD-DELETED — no tombstone, no
 * `cancelled_at`, no amount history. So churn is invisible in live data and the
 * append-only `mrr_snapshots` table is the only thing that makes it computable.
 * Two months of it, minimum. `describeMrrMovement` below exists so that "we do
 * not know yet" is a value a UI can render honestly instead of a zero it will
 * read as "nothing churned".
 */

import { roundCents } from "./cadence.js";

export interface MrrSnapshotRow {
  source: string;
  account_id: string;
  amount: number;
}

export interface MrrMovement {
  priorMrr: number;
  currentMrr: number;
  netChange: number;
  newMrr: number;
  expansionMrr: number;
  /** Negative — MRR lost to downgrades. */
  contractionMrr: number;
  /** Negative — MRR lost to churn. */
  churnedMrr: number;
  newCount: number;
  churnedCount: number;
}

/**
 * Why movement could not be computed. Each of these is a DIFFERENT sentence on
 * screen, and collapsing them into one "no data" would hide the only one that
 * is actionable (`cron_never_ran`).
 */
export type MrrMovementUnavailableReason =
  /** No snapshot rows at all — the monthly capture has never produced a row. */
  | "no_snapshots"
  /**
   * The current month has rows but no prior month does. Expected for the first
   * month after the capture starts, and not a fault.
   */
  | "no_prior_month"
  /**
   * A prior month exists but this month has nothing yet — the capture has not
   * run since the month turned over. This one IS worth looking at.
   */
  | "no_current_month";

export type MrrMovementResult =
  | { readonly status: "ok"; readonly movement: MrrMovement }
  | { readonly status: "unavailable"; readonly reason: MrrMovementUnavailableReason };

const num = (v: number | string | null | undefined): number => {
  const parsed = typeof v === "number" ? v : Number(v);
  return Number.isFinite(parsed) ? parsed : 0;
};

function keyOf(row: MrrSnapshotRow): string {
  return `${row.source}:${row.account_id}`;
}

function sumByKey(rows: readonly MrrSnapshotRow[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(keyOf(row), (map.get(keyOf(row)) ?? 0) + num(row.amount));
  }
  return map;
}

export function computeMrrMovement(
  prior: readonly MrrSnapshotRow[],
  current: readonly MrrSnapshotRow[]
): MrrMovement {
  const priorMap = sumByKey(prior);
  const currentMap = sumByKey(current);

  let newMrr = 0;
  let expansionMrr = 0;
  let contractionMrr = 0;
  let churnedMrr = 0;
  let newCount = 0;
  let churnedCount = 0;

  for (const [key, curAmount] of currentMap) {
    const priorAmount = priorMap.get(key);
    if (priorAmount === undefined) {
      newMrr += curAmount;
      newCount += 1;
    } else if (curAmount > priorAmount) {
      expansionMrr += curAmount - priorAmount;
    } else if (curAmount < priorAmount) {
      contractionMrr += curAmount - priorAmount; // negative
    }
  }

  for (const [key, priorAmount] of priorMap) {
    if (!currentMap.has(key)) {
      churnedMrr -= priorAmount; // negative
      churnedCount += 1;
    }
  }

  const priorMrr = Array.from(priorMap.values()).reduce((s, v) => s + v, 0);
  const currentMrr = Array.from(currentMap.values()).reduce((s, v) => s + v, 0);

  return {
    priorMrr: roundCents(priorMrr),
    currentMrr: roundCents(currentMrr),
    netChange: roundCents(currentMrr - priorMrr),
    newMrr: roundCents(newMrr),
    expansionMrr: roundCents(expansionMrr),
    contractionMrr: roundCents(contractionMrr),
    churnedMrr: roundCents(churnedMrr),
    newCount,
    churnedCount,
  };
}

/**
 * `computeMrrMovement`, but it refuses to answer when the history cannot
 * support an answer.
 *
 * This is the function a server route or a screen should call. `computeMrrMovement`
 * on an empty prior month happily returns a movement where every dollar is
 * `newMrr` and `churnedMrr` is 0 — arithmetically correct and a lie in
 * substance: nothing was "won" that month, we simply started measuring. On a
 * phone that renders as "$0 churned", which is precisely the number an owner
 * would act on.
 */
export function describeMrrMovement(
  prior: readonly MrrSnapshotRow[],
  current: readonly MrrSnapshotRow[]
): MrrMovementResult {
  if (prior.length === 0 && current.length === 0) {
    return { status: "unavailable", reason: "no_snapshots" };
  }
  if (prior.length === 0) return { status: "unavailable", reason: "no_prior_month" };
  if (current.length === 0) return { status: "unavailable", reason: "no_current_month" };
  return { status: "ok", movement: computeMrrMovement(prior, current) };
}

/** The sentence to put on screen for each unavailable reason. */
export const MRR_MOVEMENT_UNAVAILABLE_COPY: Readonly<
  Record<MrrMovementUnavailableReason, string>
> = {
  no_snapshots:
    "No MRR history has been captured yet, so nothing can be compared. The monthly snapshot builds it.",
  no_prior_month:
    "Only one month of MRR history exists so far. Movement needs two — check back after the next monthly snapshot.",
  no_current_month:
    "This month has no MRR snapshot yet, so nothing can be compared against last month.",
};

/**
 * The four movement components, in the order a book is read: what came in, what
 * grew, what shrank, what left. Shared so Hub and the phone cannot disagree
 * about the order or the signs.
 */
export type MrrMovementComponent = "new" | "expansion" | "contraction" | "churn";

export interface MrrMovementBreakdownEntry {
  readonly component: MrrMovementComponent;
  readonly label: string;
  /** Signed: `new`/`expansion` positive, `contraction`/`churn` negative. */
  readonly amount: number;
  /** How many accounts — only meaningful for `new` and `churn`. */
  readonly count: number | null;
}

export function mrrMovementBreakdown(
  movement: MrrMovement
): readonly MrrMovementBreakdownEntry[] {
  return [
    { component: "new", label: "New", amount: movement.newMrr, count: movement.newCount },
    { component: "expansion", label: "Expansion", amount: movement.expansionMrr, count: null },
    {
      component: "contraction",
      label: "Contraction",
      amount: movement.contractionMrr,
      count: null,
    },
    { component: "churn", label: "Churn", amount: movement.churnedMrr, count: movement.churnedCount },
  ];
}
